/* eslint-disable no-restricted-syntax */
import gql from 'graphql-tag'
import {
  FishTypeFragment,
  LakeFragment,
  LakePreviewFragment,
  WaterbodySpeciesFragment,
  WaterFeatureFragment,
  FisheryInfoFragment,
} from '@/fish/lib/graph/queries/fragments'
import { WaterbodyAttribute } from '@onxmaps/supergraph'

/**
 **** Define GQL requests
 */

/**
 * GQL query for fetching all waterbodies within a specified bounding box
 * @returns GraphQL query
 */
export const getWaterbodiesByBBox = (allFilters) => {
  const { fishExpression, accessExpression, areaExpression } = allFilters
  return gql`
    query WaterbodiesByBboxQuery(
      $bottom: Float!
      $left: Float!
      $right: Float!
      $top: Float!
      $limit: Int!
      $cursor: String
    ) {
      waterbodiesConnection(
        filter: {
          location: {
            withinBounds: {
              bottom: $bottom
              left: $left
              right: $right
              top: $top
            }
          }
          attributes: {
            contains: ${WaterbodyAttribute.FishSurveyData}
          }
          typenames: ["Lake"]
          ${fishExpression}
          ${areaExpression}
          ${accessExpression}
        }
        limit: $limit
        after: $cursor
      ) {
        edges {
          node {
            ... on Lake {
              ...lake_preview_fragment
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    ${LakePreviewFragment}
  `
}

/**
 * GQL query for fetching a single Waterbody
 * @returns GraphQL query
 */
export const getWaterbodyById = () => {
  return gql`
    query WaterbodyByIdQuery($id: ID!) {
      waterbodies(filter: { id: $id }, limit: 1) {
        ...lake_fragment
        fishingRegions {
          ...fishery_info_fragment
          partialWaterbodyCoverage
          fishSpeciesFishingTips {
            fishType {
              slug
            }
            fishingTips {
              advice(format: HTML)
            }
          }
        }
        fishingAreasConnection {
          count
        }
      }
    }
    ${LakeFragment}
    ${FisheryInfoFragment}
  `
}

/**
 * GQL query for fetching survey data for a single waterbody
 * @returns GraphQL query
 */
export const getWaterbodySurveysById = () => {
  return gql`
    query WaterbodiesSurveysByIdQuery($id: ID!) {
      waterbodies(filter: { id: $id, typenames: ["Lake"] }, limit: 1) {
        name
        ... on Lake {
          fishSurveySummaries {
            ...waterbody_species_fragment
          }
          fishingRegions {
            fishSpeciesFishingTips {
              fishType {
                slug
              }
              fishingTips {
                advice(format: HTML)
              }
            }
          }
        }
      }
    }
    ${WaterbodySpeciesFragment}
  `
}

/**
 * GQL query for fetching survey data for a single species within a single waterbody
 * @returns GraphQL query
 */
export const getWaterbodyIdSpeciesIdSurvey = () => {
  return gql`
    query WaterbodiesSpeciesIdQuery(
      $id: ID!
      $filter: WaterbodyFishManagementDataFilter!
      $summaryFilter: FishSurveySummaryFilter
      $tipsFilter: FishSpeciesFishingTipsFilter
    ) {
      waterbodies(filter: { id: $id, typenames: ["Lake", "River"] }, limit: 1) {
        ... on River {
          name
          id
          fishSurveySummaries(filter: $summaryFilter) {
            ...waterbody_species_fragment
          }
        }
        ... on Lake {
          name
          id
          fishingAuthorities {
            name
          }
          # keeperPotential lives on FishSurveySummary, not fishManagementData
          fishSurveySummaries(filter: $summaryFilter) {
            keeperPotential
          }
          fishManagementData(filter: $filter) {
            __typename
            abundanceSummary {
              surveyMethod
              trend
              trendLineStart {
                year
                value
              }
              trendLineEnd {
                year
                value
              }
              classQuartile {
                lowerBound
                lowerQuartile
                median
                upperQuartile
                upperBound
              }
              stateQuartile {
                lowerBound
                lowerQuartile
                median
                upperQuartile
                upperBound
              }
              mostRecentFishAbundanceSurvey {
                abundanceRating
                surveyDate
                costPerUnitEffort
              }
            }
            abundanceHistory {
              id
              surveyDate
              abundanceRating
              costPerUnitEffort
            }
            trophyFishRecords {
              surveyYear
              largestSurveyedLength
            }
            fishType {
              name
              slug
              image {
                contentUrl
              }
            }
            lastStocked
            stockingEffort
            stockingHistory {
              lifeStage
              countSeries {
                year
                value
              }
              lengthSeries {
                year
                value
              }
              weightSeries {
                year
                value
              }
            }
            surveyHistory {
              id
              stateSurveyId
              surveyDate
              minimumLength
              weightedAverageLength
              maximumLength
              lengthFrequencyDistributions {
                rangeStart
                rangeEnd
                frequency
              }
            }
          }
          fishingRegions {
            id
            name
            identifiers
            fishSpeciesFishingTips(filter: $tipsFilter) {
              fishType {
                slug
              }
              fishingTips {
                advice(format: HTML)
              }
            }
          }
        }
      }
    }
    ${WaterbodySpeciesFragment}
  `
}

/**
 * GQL query for fetching a larger limit of solely waterbody IDs within a specified bounding box
 * @returns GraphQL query
 */
export const getWaterbodyIDsByBbox = (allFilters) => {
  const { fishExpression, accessExpression, areaExpression } = allFilters

  return gql`
    query WaterbodyIdsByBboxQuery(
      $bottom: Float!
      $left: Float!
      $right: Float!
      $top: Float!
      $limit: Int!
    ) {
      waterbodyIDs(
        filter: {
          location: {
            withinBounds: {
              bottom: $bottom
              left: $left
              right: $right
              top: $top
            }
          }
          attributes: { intersects: [ ${WaterbodyAttribute.FishSurveyData}, ${WaterbodyAttribute.FishStockingData} ]}
          typenames: ["Lake"]
          ${fishExpression}
          ${areaExpression}
          ${accessExpression}
        }
        limit: $limit
      ) {
        __typename
        id
      }
    }
  `
}

/**
 * GQL query for fetching waterbodies matching an onX Guide `queryWaterbodies`
 * tool filter (species/location-radius/area). Field selection is kept lean —
 * this feeds an LLM context window, not a UI card.
 * @returns GraphQL query
 */
export const getWaterbodiesByFilter = () => {
  return gql`
    query WaterbodiesByFilterQuery($filter: WaterbodyFilter!, $limit: Int!) {
      waterbodies(filter: $filter, limit: $limit) {
        __typename
        id
        name
        area
        location
        latestFishSurveyYear
        trophyFish {
          slug
        }
        abundantFish {
          slug
        }
        keeperFish {
          slug
        }
        highStockingEffortFish {
          slug
        }
      }
    }
  `
}

/**
 * *(AI-generated)*
 * GQL query for fetching fishing areas for a given waterbody.
 */
export const getFishingAreasByWaterbody = () => {
  return gql`
    query getFishingAreasByWaterbodyQuery(
      $id: ID!
      $cursor: String
      $limit: Int
    ) {
      waterbodies(filter: { id: $id, typenames: ["Lake", "River"] }, limit: 1) {
        fishingAreasConnection(limit: $limit, after: $cursor) {
          edges {
            node {
              id
              identifiers
              label
              name
              fishTypes {
                ...fish_type_fragment
              }
              waterFeatures {
                ...water_feature_fragment
              }
              nonNullableWaterbody: waterbody {
                ... on Lake {
                  id
                  name
                }
                ... on River {
                  id
                  name
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    ${FishTypeFragment}
    ${WaterFeatureFragment}
  `
}

/**
 * GQL query for fetching stocking insights per species
 * @returns GraphQL query
 */
export const getStockingInsightsBySpecies = () => {
  return gql`
    query FishTypes($species: String) {
      fishTypes(filter: { slug: { eq: $species } }) {
        stockingInsights(format: STRIP)
        name
      }
    }
  `
}

/**
 * *(AI-generated)*
 * GQL query for fetching fishing regions for a given waterbody.
 */
export const getFishingRegionsByWaterbody = () => {
  return gql`
    query getFishingRegionsByWaterbodyQuery($id: ID!) {
      waterbodiesConnection(
        filter: { id: $id, typenames: ["Lake", "River"] }
        limit: 1
      ) {
        edges {
          node {
            id
            name
            maxDepth
            attributes
            latestFishSurveyYear
            trophyFish {
              slug
            }
            fishingRegions {
              __typename
              name
              id
              location
              area
              coverPhoto {
                contentUrl
              }
              aboutConnection {
                count
              }
            }
          }
        }
      }
    }
  `
}
